'use client'

import { NativeProductSelect } from '../native-product-select'

export interface MobilePriceLevelOption {
  id: number
  name: string
  sortOrder: number
  lowestPrice: string | null
}

export interface MobilePriceModelOption {
  id: number
  name: string
  levels: MobilePriceLevelOption[]
}

interface MobilePriceConfigurationSelectProps {
  models: MobilePriceModelOption[]
  selectedModelId: number
  selectedLevelId: number
  ariaLabel: string
  onValueChange: (modelId: number, levelId: number) => void
}

const ChevronIcon = ({ className = '' }: { className?: string }) => (
  <svg aria-hidden="true" viewBox="0 0 16 10" fill="none" className={`h-2.5 w-4 ${className}`}>
    <path d="m1.5 1.5 6.5 6.5 6.5-6.5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
  </svg>
)

export const MobilePriceConfigurationSelect = ({
  models,
  selectedModelId,
  selectedLevelId,
  ariaLabel,
  onValueChange,
}: MobilePriceConfigurationSelectProps) => {
  const selectedModel = models.find((model) => model.id === selectedModelId) ?? models[0]
  const selectedLevel = selectedModel?.levels.find((level) => level.id === selectedLevelId) ?? selectedModel?.levels[0]
  const showModelGroups = models.length > 1

  if (!selectedModel || !selectedLevel) {
    return null
  }

  return (
    <NativeProductSelect
      value={`${selectedModel.id}:${selectedLevel.id}`}
      ariaLabel={ariaLabel}
      options={models.map((model) => {
        const items = model.levels.map((level) => (
          <option key={`${model.id}:${level.id}`} value={`${model.id}:${level.id}`}>
            {level.name}
            {level.lowestPrice ? ` · ${level.lowestPrice}` : ''}
          </option>
        ))
        return showModelGroups ? (
          <optgroup key={model.id} label={model.name}>
            {items}
          </optgroup>
        ) : (
          items
        )
      })}
      onValueChange={(value) => {
        const [modelIdText, levelIdText] = value.split(':')
        const modelId = Number(modelIdText)
        const levelId = Number(levelIdText)
        if (models.some((model) => model.id === modelId && model.levels.some((level) => level.id === levelId))) {
          onValueChange(modelId, levelId)
        }
      }}
      className="border-navy/15 text-navy focus-visible:border-lapis focus-visible:ring-lapis/20 flex h-[52px] w-full cursor-pointer items-center gap-3 rounded-xl border bg-white px-4 text-left shadow-[0_2px_8px_rgba(20,37,70,0.06)] transition-[border-color,box-shadow] outline-none focus-visible:ring-4"
    >
      <span className="flex min-w-0 flex-1">
        <span className="flex min-w-0 flex-1 items-baseline gap-1.5">
          {showModelGroups && (
            <>
              <span className="truncate text-base font-semibold">{selectedModel.name}</span>
              <span aria-hidden="true" className="text-navy-30">
                ·
              </span>
            </>
          )}
          <span className={showModelGroups ? 'shrink-0 text-sm font-medium' : 'truncate text-base font-semibold'}>
            {selectedLevel.name}
          </span>
        </span>
      </span>
      {selectedLevel.lowestPrice && (
        <span className="text-navy-60 ml-auto shrink-0 text-sm font-medium tabular-nums">
          {selectedLevel.lowestPrice}
        </span>
      )}
      <span className="text-navy-60 shrink-0">
        <ChevronIcon />
      </span>
    </NativeProductSelect>
  )
}
